import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  CircularProgress,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  Switch,
  FormControlLabel,
  Chip,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Delete, Edit, Add, FilterAltOff } from '@mui/icons-material';
import { districtsAPI, citiesAPI, languageSupportAPI } from '../../services/api';
import { clearFilterButtonSx } from '../../utils/clearFilterButtonSx';
import { useLanguage } from '../../context/LanguageContext';
import { getAdminUser } from '../../utils/authSessionStorage';
import browserGlobal from '../../utils/browserGlobal';

const defaultTranslation = () => ({ languageCode: 'en', name: '' });


const Districts = () => {
  const { t } = useLanguage();
  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down('sm'));
  const adminUser = getAdminUser();
  const isSuperAdmin = /super\s*admin/i.test(adminUser.role || '');

  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toast, setToast] = useState({ text: '', type: 'info' });
  const [open, setOpen] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    cityId: '',
    status: 'active',
    search: ''
  });

  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    debouncedSearchRef.current = debouncedSearch;
  }, [debouncedSearch]);

  useEffect(() => {
    const id = browserGlobal.setTimeout(() => {
      const next = (filters.search || '').trim();
      const prev = debouncedSearchRef.current;
      if (prev !== next) {
        setPage(0);
      }
      setDebouncedSearch(next);
    }, 400);
    return () => browserGlobal.clearTimeout(id);
  }, [filters.search]);

  const [formData, setFormData] = useState({
    name: '',
    cityId: '',
    isEnabled: 1,
    translations: [defaultTranslation()],
  });
  const [languages, setLanguages] = useState([]);
  const showToast = (text, type = 'info') => {
    setToast({ text, type });
    setToastOpen(true);
  };

  const fetchLanguages = async () => {
    try {
      const response = await languageSupportAPI.getLanguages(0, 500);
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const list = (response.data.payload || []).filter((l) => l.isActive !== false);
        setLanguages(list);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error fetching languages:', error);
      setLanguages([]);
    }
  };

  const fetchCities = async () => {
    try {
      const response = await citiesAPI.getCities();
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const citiesList = response.data.payload;
        setCities(citiesList);

        if (citiesList.length > 0) {
          const firstCity = citiesList[0];
          setFilters(prev => ({
            ...prev,
            cityId: firstCity.id
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const fetchDistricts = useCallback(async () => {
    if (!filters.cityId) return;
    setIsLoading(true);
    try {
      const response = await districtsAPI.getDistricts(filters.cityId, page, rowsPerPage, {
        search: debouncedSearch,
      });
      if (response?.data?.success === true && Array.isArray(response.data.payload)) {
        const districtsList = response.data.payload;
        setDistricts(districtsList);
        const apiTotal = response.data.totalRecord;
        setTotalCount(
          apiTotal != null && Number(apiTotal) >= 0 ? Number(apiTotal) : districtsList.length,
        );
      } else {
        setDistricts([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching districts:', error);
      setDistricts([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters.cityId, page, rowsPerPage, debouncedSearch]);

  const handleClearFilters = () => {
    const firstCityId = cities.length > 0 ? cities[0].id : '';
    setFilters({
      cityId: firstCityId,
      status: 'active',
      search: ''
    });
    setDebouncedSearch('');
    setPage(0);
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchLanguages();
  }, []);

  useEffect(() => {
    if (filters.cityId) {
      fetchDistricts();
    }
  }, [filters.cityId, fetchDistricts]);

  const visibleDistricts = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return districts;
    const hay = (v) => String(v ?? '').toLowerCase();
    return districts.filter((d) => {
      if (hay(d.name).includes(q)) return true;
      const cityLabel = cities.find((c) => c.id === d.cityId)?.name;
      if (hay(cityLabel).includes(q)) return true;
      const trs = Array.isArray(d.translations) ? d.translations : [];
      return trs.some((tr) => hay(tr.name).includes(q));
    });
  }, [districts, debouncedSearch, cities]);

  const handleOpen = (district = null) => {
    if (district) {
      setSelectedDistrict(district);
      setFormData({
        name: district.name,
        cityId: district.cityId,
        isEnabled: district.isEnabled ? 1 : 0,
        translations: (district.translations && district.translations.length)
          ? district.translations.map((t) => ({ languageCode: t.languageCode || '', name: t.name || '' }))
          : [defaultTranslation()],
      });
    } else {
      setSelectedDistrict(null);
      setFormData({
        name: '',
        cityId: filters.cityId || '',
        isEnabled: 1,
        translations: [defaultTranslation()],
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedDistrict(null);
  };



  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTranslationChange = (index, field, value) => {
    setFormData((prev) => {
      const next = [...prev.translations];
      next[index] = { ...next[index], [field]: value };
      return { ...prev, translations: next };
    });
  };

  const addTranslation = () => {
    setFormData((prev) => ({
      ...prev,
      translations: [...prev.translations, defaultTranslation()],
    }));
  };

  const removeTranslation = (index) => {
    if (formData.translations.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      translations: prev.translations.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const translations = formData.translations
        .filter(t => t.languageCode.trim() && t.name.trim())
        .map(t => ({
          languageCode: t.languageCode.trim(),
          name: t.name.trim()
        }));

      const payload = {
        ...formData,
        name: formData.name.trim(),
        translations
      };

      if (selectedDistrict) {
        await districtsAPI.updateDistrict({ ...payload, id: selectedDistrict.id });
        showToast(t('districts.toastUpdated'), 'success');
      } else {
        await districtsAPI.createDistrict(payload);
        showToast(t('districts.toastCreated'), 'success');
      }
      fetchDistricts();
      handleClose();
    } catch (error) {
      console.error('Error saving district:', error);
      showToast(error?.response?.data?.errorMessage || t('districts.toastSaveError'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (browserGlobal.confirm(t('districts.confirmDelete'))) {
      setIsLoading(true);
      try {
        await districtsAPI.deleteDistrict(id);
        showToast(t('districts.toastDeleted'), 'success');
        fetchDistricts();
      } catch (error) {
        console.error('Error deleting district:', error);
        showToast(error?.response?.data?.errorMessage || t('districts.toastDeleteError'), 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <Box p={1}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        mb={2}
        flexDirection={{ xs: 'column', sm: 'row' }}
        flexWrap="wrap"
        gap={1}
      >
        <Typography variant="h4" color="primary">
          {t('districts.title')}
        </Typography>
        {isSuperAdmin && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpen()}
            sx={{
              background: 'linear-gradient(135deg, #1b5a5c 0%, #0a2829 100%)',
              color: '#ffffff',
              '&:hover': {
                background: 'linear-gradient(135deg, #328284 0%, #0F3D3E 100%)',
              },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            {t('districts.add')}
          </Button>
        )}
      </Box>

      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: '#fff',
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('districts.filterCity')}</InputLabel>
              <Select
                value={filters.cityId}
                label={t('districts.filterCity')}
                onChange={(e) => {
                  setFilters(prev => ({
                    ...prev,
                    cityId: e.target.value,
                  }));
                  setPage(0);
                }}
              >
                <MenuItem value="">
                </MenuItem>
                {cities.map((city) => (
                  <MenuItem key={city.id} value={city.id}>
                    {city.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              size="small"
              fullWidth
              placeholder={t('districts.searchPlaceholder')}
              value={filters.search}
              onChange={(e) => {
                setFilters((prev) => ({
                  ...prev,
                  search: e.target.value,
                }));
              }}
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={6}
            md="auto"
            sx={{ display: 'flex', alignItems: 'center', ml: { md: 'auto' } }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterAltOff />}
              onClick={handleClearFilters}
              sx={{ ...clearFilterButtonSx, width: { xs: '100%', md: 'auto' } }}
            >
              {t('common.clear')}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress sx={{ color: '#0F3D3E' }} />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            overflowX: 'auto',
            boxShadow: '0 2px 4px rgba(124, 58, 237, 0.05)',
            borderRadius: 2,
            '& .MuiTableCell-head': {
              backgroundColor: '#ebf4f4',
              color: '#0F3D3E',
              fontWeight: 600,
              padding: '16px'
            },
            '& .MuiTableCell-body': {
              padding: '16px'
            }
          }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('common.name')}</TableCell>
                <TableCell>{t('common.city')}</TableCell>
                <TableCell>{t('common.translations')}</TableCell>
                <TableCell>{t('common.status')}</TableCell>
                <TableCell>{t('common.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleDistricts.map((district) => (
                <TableRow
                  key={district.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: '#ebf4f4',
                    }
                  }}
                >
                  <TableCell>{district.name}</TableCell>
                  <TableCell>{cities.find(c => c.id === district.cityId)?.name || t('common.unknown')}</TableCell>
                  <TableCell>
                    {district.translations?.length
                      ? district.translations.map((tr) => `${tr.languageCode}: ${tr.name}`).join(', ')
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={district.isEnabled === 1 ? t('common.active') : t('common.inactive')}
                      color={district.isEnabled === 1 ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>

                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <IconButton
                        onClick={() => handleOpen(district)}
                        color="primary"
                        size="small"
                        title={t('common.edit')}
                      >
                        <Edit />
                      </IconButton>
                      {isSuperAdmin && (
                        <IconButton
                          onClick={() => handleDelete(district.id)}
                          color="error"
                          size="small"
                          title={t('common.delete')}
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number.parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage={t('common.rowsPerPage')}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{
              '.MuiTablePagination-select': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-selectIcon': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-displayedRows': {
                color: '#0F3D3E',
              },
              '.MuiTablePagination-actions': {
                '& .MuiIconButton-root': {
                  color: '#0F3D3E',
                },
                '& .Mui-disabled': {
                  color: '#e9d5ff',
                }
              }
            }}
          />
        </TableContainer>
      )}

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" fullScreen={isMobileView}>
        <DialogTitle>{selectedDistrict ? t('districts.dialogEdit') : t('districts.dialogAdd')}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('common.city')}</InputLabel>
            <Select
              name="cityId"
              value={formData.cityId}
              label={t('common.city')}
              onChange={handleChange}
            >
              {cities.map((city) => (
                <MenuItem key={city.id} value={city.id}>{city.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            name="name"
            label={t('districts.fieldDistrictName')}
            fullWidth
            value={formData.name}
            onChange={handleChange}
          />

          <Box mt={3}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('common.translationsRequired')}
            </Typography>
            {formData.translations.map((tr, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-start',
                  mb: 2,
                }}
              >
                <FormControl required size="small" sx={{ flex: 1 }}>
                  <InputLabel id={`lang-select-${index}`}>{t('common.language')}</InputLabel>
                  <Select
                    labelId={`lang-select-${index}`}
                    label={t('common.language')}
                    value={tr.languageCode || ''}
                    onChange={(e) => handleTranslationChange(index, 'languageCode', e.target.value)}
                  >
                    {languages.map((lang) => (
                      <MenuItem key={lang.id || lang.languageCode} value={lang.languageCode || ''}>
                        {lang.name || lang.languageCode || ''}
                      </MenuItem>
                    ))}
                    {tr.languageCode && !languages.some((l) => (l.languageCode || '') === tr.languageCode) && (
                      <MenuItem value={tr.languageCode}>{tr.languageCode}</MenuItem>
                    )}
                  </Select>
                </FormControl>
                <TextField
                  required
                  size="small"
                  label={t('common.name')}
                  value={tr.name}
                  onChange={(e) => handleTranslationChange(index, 'name', e.target.value)}
                  placeholder={t('common.translatedNamePh')}
                  sx={{ flex: 1 }}
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeTranslation(index)}
                  disabled={formData.translations.length <= 1}
                >
                  <Delete />
                </IconButton>
              </Box>
            ))}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={addTranslation}
              sx={{ mt: 1, color: '#0F3D3E' }}
            >
              {t('common.addTranslation')}
            </Button>
          </Box>

          <Box mt={2}>
            <FormControlLabel
              control={
                <Switch
                  name="isEnabled"
                  checked={formData.isEnabled === 1}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      isEnabled: e.target.checked ? 1 : 0
                    }));
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: '#0F3D3E',
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: '#0F3D3E',
                    },
                  }}
                />
              }
              label={t('common.active')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={isSubmitting}>{t('common.cancel')}</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : null}
          >
            {selectedDistrict ? t('common.update') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toastOpen}
        autoHideDuration={3000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toast.type} sx={{ width: '100%' }}>
          {toast.text}
        </Alert>
      </Snackbar>


    </Box>
  );
};

export default Districts;
